import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../../lib/store'
import { billingApi, type UpsellOffer } from '../../lib/api'
import { PLANT_UPSELL_EVENT } from '../../lib/upsell'
import { UpgradeModal } from './UpgradeModal'

// App-wide gate for the plant-limit upgrade modal (§242). Mounted once in the
// authed shell. Two triggers:
//  • PROACTIVE — polls GET /billing/upsell; auto-shows when the tenant has been
//    sustained at the cap ≥2 days (offer.eligible), respecting a client-side
//    "maybe later" snooze.
//  • REACTIVE — a plant create that 402'd fires PLANT_UPSELL_EVENT; we fetch a
//    fresh offer and show immediately (a hard wall — ignores the snooze).
const SNOOZE_KEY = 'grafty-upsell-snooze'
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000 // 3 days

function snoozed(): boolean {
  try {
    const v = localStorage.getItem(SNOOZE_KEY)
    return v ? Date.now() < Number(v) : false
  } catch {
    return false
  }
}
function setSnooze(): void {
  try { localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS)) } catch { /* ignore */ }
}

export function UpsellGate() {
  const { token, role } = useAuthStore()
  const enabled = !!token && role !== 'superuser' // superusers have no tenant/billing
  const [open, setOpen] = useState(false)
  const [offer, setOffer] = useState<UpsellOffer | null>(null)
  // Auto-show at most once per eligibility "episode" — reset only when the tenant
  // is no longer eligible. Prevents re-opening after a manual close or a successful
  // in-place upgrade (which closes without snoozing, while `data` is briefly stale).
  const shownRef = useRef(false)

  const { data } = useQuery({
    queryKey: ['upsell'],
    queryFn: () => billingApi.upsell().then((r) => r.data),
    enabled,
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  })

  // Proactive auto-show.
  useEffect(() => {
    if (!data?.eligible) { shownRef.current = false; return } // new episode can re-show
    if (!open && !shownRef.current && data.suggested_plan && !snoozed()) {
      shownRef.current = true
      setOffer(data)
      setOpen(true)
    }
  }, [data, open])

  // Reactive: fetch fresh + force-show on a plant-limit 402.
  useEffect(() => {
    if (!enabled) return
    const handler = () => {
      billingApi.upsell()
        .then((r) => { if (r.data.suggested_plan) { setOffer(r.data); setOpen(true) } })
        .catch(() => { /* ignore */ })
    }
    window.addEventListener(PLANT_UPSELL_EVENT, handler)
    return () => window.removeEventListener(PLANT_UPSELL_EVENT, handler)
  }, [enabled])

  const close = useCallback((snooze: boolean) => {
    if (snooze) setSnooze()
    // Mark this episode handled so the proactive effect won't re-open with stale
    // (still-eligible) data — notably right after a successful in-place upgrade,
    // which closes WITHOUT a snooze while the ['upsell'] refetch is in flight. The
    // ref resets when the tenant is no longer eligible (a genuine new episode). A
    // fresh plant-limit 402 still re-shows via the reactive listener (bypasses this).
    shownRef.current = true
    setOpen(false)
  }, [])

  if (!enabled || !open || !offer) return null
  return <UpgradeModal offer={offer} onClose={close} />
}
